import AxiosInstance from "service/axiosInstance";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "hooks";

const getDropdown = () =>
  AxiosInstance({ method: "GET", url: "api/v1/payment-source/categories/dropdown" });

export const useGetCategoryDropdown = () =>
  useQuery({
    queryKey: ["category-dropdown"],
    queryFn: getDropdown,
    staleTime: 0,
  });

// ── Main category (PaymentSource) ──────────────────────────────────────────

const createPaymentSource = (data) =>
  AxiosInstance({ method: "POST", url: "api/v1/payment-source", data });

export const useCreatePaymentSource = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation(createPaymentSource, {
    onSuccess: (res) => {
      queryClient.invalidateQueries(["category-dropdown"]);
      toast.toastSuccess(res?.message || "Category created");
    },
    onError: (err) => toast.toastError(err?.message || "Failed to create category"),
  });
};

const toggleSourceVisibility = (id) =>
  AxiosInstance({ method: "PATCH", url: `api/v1/payment-source/${id}/toggle-visibility` });

export const useToggleSourceVisibility = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation(toggleSourceVisibility, {
    onSuccess: (res) => {
      queryClient.invalidateQueries(["category-dropdown"]);
      toast.toastSuccess(res?.message || "Visibility updated");
    },
    onError: (err) => toast.toastError(err?.message || "Failed to update visibility"),
  });
};

// ── Sub-category (TransactionCategory) ────────────────────────────────────

const createCategory = ({ paymentSourceId, name }) =>
  AxiosInstance({
    method: "POST",
    url: `api/v1/payment-source-category/${paymentSourceId}`,
    data: { name },
  });

export const useCreateCategory = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation(createCategory, {
    onSuccess: (res) => {
      queryClient.invalidateQueries(["category-dropdown"]);
      toast.toastSuccess(res?.message || "Sub-category added");
    },
    onError: (err) => toast.toastError(err?.message || "Failed to add sub-category"),
  });
};

const toggleVisibility = ({ paymentSourceId, id }) =>
  AxiosInstance({
    method: "PATCH",
    url: `api/v1/payment-source-category/${paymentSourceId}/${id}/toggle-visibility`,
  });

export const useToggleVisibility = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation(toggleVisibility, {
    onSuccess: (res) => {
      queryClient.invalidateQueries(["category-dropdown"]);
      toast.toastSuccess(res?.message || "Visibility updated");
    },
    onError: (err) => toast.toastError(err?.message || "Failed to update visibility"),
  });
};

// ── Icon upload ────────────────────────────────────────────────────────────

const uploadIcon = ({ id, file }) => {
  const formData = new FormData();
  formData.append("icon", file);
  return AxiosInstance({ method: "PATCH", url: `api/v1/payment-source/${id}/icon`, data: formData });
};

export const useUploadIcon = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation(uploadIcon, {
    onSuccess: (res) => {
      queryClient.invalidateQueries(["category-dropdown"]);
      toast.toastSuccess(res?.message || "Icon updated");
    },
    onError: (err) => toast.toastError(err?.message || "Failed to upload icon"),
  });
};

// ── Transaction Type Config ────────────────────────────────────────────────
const getTransactionTypes = () =>
  AxiosInstance({ method: "GET", url: "api/v1/transaction-type-config" });

export const useGetTransactionTypes = () =>
  useQuery({
    queryKey: ["transaction-type-config"],
    queryFn: getTransactionTypes,
    staleTime: 0,
  });

const createTransactionType = (data) =>
  AxiosInstance({ method: "POST", url: "api/v1/transaction-type-config", data });

export const useCreateTransactionType = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation(createTransactionType, {
    onSuccess: (res) => {
      queryClient.invalidateQueries(["transaction-type-config"]);
      toast.toastSuccess(res?.message || "Transaction type created");
    },
    onError: (err) => toast.toastError(err?.message || "Failed to create type"),
  });
};

const toggleTransactionType = (id) =>
  AxiosInstance({ method: "PATCH", url: `api/v1/transaction-type-config/${id}/toggle-visibility` });

export const useToggleTransactionType = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation(toggleTransactionType, {
    onSuccess: (res) => {
      queryClient.invalidateQueries(["transaction-type-config"]);
      toast.toastSuccess(res?.message || "Visibility updated");
    },
    onError: (err) => toast.toastError(err?.message || "Failed to update"),
  });
};
